/**
 * Version_Checker (auto-reload-on-deploy)
 *
 * A service-worker-free, singleton runtime module that detects when a newer
 * build has been deployed. It reads the boot version inlined at build time as
 * `window.__APP_VERSION__`, periodically fetches a non-hashed `/version.json`,
 * compares the deployed version against the boot version, records an
 * "update pending" flag, exposes that state programmatically, logs detection
 * once, and broadcasts a detection event on the injected global event bus.
 *
 * IMPORTANT: This module imports nothing from `src/main.js`. The event `bus`
 * is injected via `initVersionCheck({ bus })` to avoid a circular import
 * (main.js -> router -> versionCheck -> main.js). See design.md "Version_Checker".
 *
 * This file (task 6.1) establishes the module structure, singleton state, and
 * the public query interface. The `initVersionCheck` / `checkVersion` / `stop`
 * behaviors are implemented in follow-on tasks 6.2 / 6.3 / 6.4 and are stubbed
 * here so the module is a valid, importable ESM unit.
 */

/**
 * Bus event name emitted when a newer deployed version is first detected.
 * Exported as a stable constant so future subscribers (e.g. a "new version
 * available" banner) can listen without depending on detection internals.
 * (Requirement 7.1)
 * @type {string}
 */
export const UPDATE_AVAILABLE_EVENT = 'app-version:update-available';

/**
 * localStorage key holding the last Boot_Version the SPA initialized with.
 * Persisted across full-page reloads to drive the post-reload confirmation
 * log (Requirement 9.2–9.5). Independent of the three Vuex stores.
 * @type {string}
 */
export const KNOWN_VERSION_KEY = 'pnn:appKnownVersion';

/**
 * Module-singleton internal state.
 *
 * @property {string|null} bootVersion       value read from `window.__APP_VERSION__` at init (Req 2.1)
 * @property {boolean}     enabled           false if `bootVersion` absent/empty (Req 2.2)
 * @property {boolean}     updatePending     Update_Pending_State (Req 5.1)
 * @property {object|null} pendingInfo       Pending_Version_Info once detected (Req 5.4)
 * @property {boolean}     checkInFlight     in-flight guard (Req 4.5)
 * @property {string|null} loggedForVersion  the Deployed_Version already logged (Req 6.1, 6.3)
 * @property {*}           intervalId        setInterval handle for teardown
 * @property {Function|null} onVisibility    visibilitychange listener handle for teardown
 * @property {object|null} bus               injected event bus (set in initVersionCheck)
 */
const state = {
  bootVersion: null,
  enabled: false,
  updatePending: false,
  pendingInfo: null,
  checkInFlight: false,
  loggedForVersion: null,
  intervalId: null,
  onVisibility: null,
  bus: null,
};

/**
 * Pure comparison helper. Returns true when the boot version and the deployed
 * version differ (i.e. an update is available). Exposed for unit testing.
 * (Requirements 5.3, 5.5 — supports detection logic.)
 *
 * @param {string|null|undefined} bootVersion
 * @param {string|null|undefined} deployedVersion
 * @returns {boolean} true when the two values are not strictly equal
 */
export function _compare(bootVersion, deployedVersion) {
  return bootVersion !== deployedVersion;
}

/**
 * Returns the current Update_Pending_State. (Requirement 5.3)
 * @returns {boolean}
 */
export function isUpdatePending() {
  return state.updatePending;
}

/**
 * Returns the recorded Pending_Version_Info, or null if no update has been
 * detected. (Requirement 5.5)
 * @returns {{current: string, next: string, releaseType: (string|null), notes: (string|null)}|null}
 */
export function getPendingVersionInfo() {
  return state.pendingInfo;
}

/**
 * Initialize the version checker once after the app is mounted. Idempotent.
 *
 * Init sequence (see design.md "Init sequence"):
 *  1. Read `bootVersion` from `window.__APP_VERSION__`. If null/undefined/empty/
 *     whitespace-only, set `enabled = false` and return without registering any
 *     trigger (Requirements 2.1, 2.2, 4.3).
 *  2. Post-reload confirmation (Requirement 9): read `known` from localStorage
 *     (key `pnn:appKnownVersion`). If `known` exists and differs from
 *     `bootVersion`, write exactly one "new code is running" log including both
 *     values (9.2). If `known` is absent, write no confirmation log (9.5). If
 *     equal, write nothing (9.3). Then always store `bootVersion` as the new
 *     known version (9.4).
 *  3. Register the Focus_Trigger (`visibilitychange` -> visible) (4.1) and start
 *     the Interval_Trigger (`setInterval`, default 300000 ms) (4.2). Both fire
 *     `checkVersion()` (implemented in task 6.3).
 *
 * Idempotent: calling twice does not double-register triggers.
 *
 * @param {{ bus: object, intervalMs?: number }} options
 * @param {object} options.bus         the global event bus (injected to avoid a circular import)
 * @param {number} [options.intervalMs=300000] interval-trigger period in ms (Requirement 4.2)
 * @returns {void}
 */
export function initVersionCheck({ bus, intervalMs = 300000 } = {}) {
  // Idempotency guard: if we have already initialized (triggers registered) or
  // explicitly disabled checking this session, do nothing on a second call.
  if (state.intervalId !== null || state.onVisibility !== null) {
    return;
  }

  // Injected bus (kept even when disabled so consumers see a consistent handle).
  state.bus = bus || null;

  // 1. Read Boot_Version (Requirement 2.1). Treat null/undefined/empty/
  //    whitespace-only as absent and disable checking for this session
  //    (Requirements 2.2, 4.3) without registering any trigger.
  const rawBootVersion =
    typeof window !== 'undefined' ? window.__APP_VERSION__ : undefined;
  const bootVersion =
    typeof rawBootVersion === 'string' ? rawBootVersion : null;

  if (bootVersion === null || bootVersion.trim() === '') {
    state.bootVersion = null;
    state.enabled = false;
    return;
  }

  state.bootVersion = bootVersion;
  state.enabled = true;

  // 2. Post-reload confirmation log (Requirement 9).
  let known = null;
  try {
    known = window.localStorage.getItem(KNOWN_VERSION_KEY);
  } catch (e) {
    // localStorage may be unavailable (privacy mode, etc.); treat as absent.
    known = null;
  }

  if (known !== null && known !== bootVersion) {
    // Known version exists and differs -> exactly one confirmation log (9.2).
    // eslint-disable-next-line no-console
    console.log(
      `[app-version] New code is running (was ${known}, now ${bootVersion})`
    );
  }
  // If `known` is absent (null) write no confirmation log (9.5); if equal write
  // nothing (9.3). In all cases, always record the current bootVersion as the
  // new known version for the next initialization (9.4).
  try {
    window.localStorage.setItem(KNOWN_VERSION_KEY, bootVersion);
  } catch (e) {
    // Ignore persistence failures; detection still works this session.
  }

  // 3. Register the Focus_Trigger (Requirement 4.1): fire a check only when the
  //    document transitions to the visible state.
  const onVisibility = () => {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      checkVersion();
    }
  };
  state.onVisibility = onVisibility;
  document.addEventListener('visibilitychange', onVisibility);

  // 3. Start the Interval_Trigger (Requirement 4.2), default 300000 ms.
  state.intervalId = setInterval(() => {
    checkVersion();
  }, intervalMs);
}

/**
 * Render a version value for the detection log. Null, undefined, and empty/
 * whitespace-only strings are rendered as the unknown-value literal so the log
 * entry is never omitted. (Requirement 6.2)
 *
 * @param {*} value
 * @returns {string}
 */
function _forLog(value) {
  if (value === null || value === undefined) {
    return '(unknown)';
  }
  if (typeof value === 'string' && value.trim() === '') {
    return '(unknown)';
  }
  return String(value);
}

/**
 * Perform a single version check (triggered by focus and interval).
 *
 * See design.md "checkVersion()":
 *  1. If `!enabled` -> return (Req 4.3).
 *  2. If `checkInFlight` -> return without starting a concurrent check (Req 4.5).
 *  3. Set `checkInFlight = true`; fetch `/version.json?t=<now>` with
 *     `cache: 'no-store'` via the native `fetch` (NOT `$http`) (Req 3.1, 3.2);
 *     read `version`, `releaseType`, `notes` from the parsed body (Req 3.3).
 *  4. If `deployed !== bootVersion` and not already pending: set
 *     `updatePending = true`; record `pendingInfo` (Req 5.1, 5.4); log detection
 *     at most once per deployed version rendering unknowns as "(unknown)"
 *     (Req 6.1, 6.2, 6.3); emit the bus event (Req 7.1). Never reload here
 *     (Req 4.6, 8.3, 8.4).
 *  5. If `deployed === bootVersion` -> leave `updatePending` unchanged (Req 5.2).
 *  6. On any fetch/parse/network error -> swallow it: leave state unchanged and
 *     suppress console error output (Req 10.1, 10.2).
 *  7. `finally { checkInFlight = false }` so the next trigger starts fresh
 *     (Req 10.3).
 *
 * @returns {Promise<void>}
 */
export async function checkVersion() {
  // 1. Disabled sessions never fetch (Req 4.3).
  if (!state.enabled) {
    return;
  }

  // 2. Ignore triggers while a check is already in flight (Req 4.5).
  if (state.checkInFlight) {
    return;
  }

  // 3. Mark in flight and fetch a fresh, cache-busted version file via the
  //    native fetch API (NOT $http, whose baseURL points at the Pinnacle API).
  state.checkInFlight = true;
  try {
    const response = await fetch('/version.json?t=' + Date.now(), {
      cache: 'no-store',
    });
    const body = await response.json();

    const deployed = body.version;
    // releaseType/notes default to null when absent (matches PendingVersionInfo).
    const releaseType =
      body.releaseType === undefined ? null : body.releaseType;
    const notes = body.notes === undefined ? null : body.notes;

    // 4/5. Compare deployed vs boot version using the shared helper.
    if (_compare(state.bootVersion, deployed) && !state.updatePending) {
      // A differing deployed version, not already pending -> record state.
      state.updatePending = true;
      state.pendingInfo = {
        current: state.bootVersion,
        next: deployed,
        releaseType,
        notes,
      };

      // Log detection at most once per detected deployed version (Req 6.1, 6.3),
      // rendering null/undefined/empty values as "(unknown)" (Req 6.2).
      if (state.loggedForVersion !== deployed) {
        // eslint-disable-next-line no-console
        console.log(
          `[app-version] Update detected (running ${_forLog(
            state.bootVersion
          )}, deployed ${_forLog(deployed)})`
        );
        state.loggedForVersion = deployed;
      }

      // Broadcast detection on the injected bus (Req 7.1). Never reload here
      // (Req 4.6, 8.3, 8.4).
      if (state.bus && typeof state.bus.$emit === 'function') {
        state.bus.$emit(UPDATE_AVAILABLE_EVENT, {
          current: state.bootVersion,
          next: deployed,
          releaseType,
          notes,
        });
      }
    }
    // deployed === bootVersion -> leave updatePending unchanged (Req 5.2).
  } catch (e) {
    // 6. Swallow fetch/parse/network errors: leave state unchanged and suppress
    //    console error output (Req 10.1, 10.2).
  } finally {
    // 7. Always clear the in-flight guard so the next trigger can start fresh
    //    (Req 10.3).
    state.checkInFlight = false;
  }
}

/**
 * Teardown (Requirement 4.4): remove the Focus_Trigger (`visibilitychange`)
 * listener and clear the Interval_Trigger so no further version checks are
 * initiated. Safe to call multiple times — after the first call both handles
 * are null, so repeated calls are no-ops.
 *
 * Resetting `intervalId` and `onVisibility` back to null also lets the module
 * be re-initialized cleanly: `initVersionCheck`'s idempotency guard keys off
 * these handles, so clearing them here restores a pristine, re-initializable
 * state.
 *
 * @returns {void}
 */
export function stop() {
  // Remove the Focus_Trigger listener if one is registered.
  if (state.onVisibility !== null) {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', state.onVisibility);
    }
    state.onVisibility = null;
  }

  // Stop the Interval_Trigger if one is running.
  if (state.intervalId !== null) {
    clearInterval(state.intervalId);
    state.intervalId = null;
  }
}

// Exported for unit-test access to the singleton state without widening the
// public API. Not intended for production consumers.
export const _state = state;
